import React, { useCallback, useEffect, useMemo } from 'react';
import i18n                                       from 'i18next';
import DV                                         from 'ts-data-validator';

import { staticPageConstants } from '../../../../common/constants/staticPageConstants';
import { useRouter }           from '../../../../common/hooks/useRouter';

import RenderButton      from '../../../components/buttons/base/RenderButton';
import ContactUs         from '../../../components/buttons/ContactUs';
import ConsultationBlock from '../../../components/consultationBlock';
import Meta              from '../../../components/meta';
import Preloader         from '../../../components/containers/preloader';
import PageHeader        from '../../../components/pageHeader';
import RenderHeaderTabs  from '../../../components/tabs/RenderHeaderTabs';
import { useCommon }     from '../../../providers/common';

import renderStaticSection from '../../_common/utils/renderStaticSection';
import Notfound            from '../../notfound/components/Notfound';

const AboutDoors = function() {
  const {
          location: { pathname },
          match:    { params: { page: section } },
        } = useRouter();
  const {
          getPageData,
          pages,
          loading,
        } = useCommon();

  const node = React.useRef(null);
  let tabs   = {};

  const currentPage = useMemo(() => {
    return pages.find((page) => `/${page.urlKey}` === pathname);
  }, [pages, pathname]);

  const page = useMemo(() => {
    return pages.find((page) => page.urlKey === section);
  }, [pages, section]);

  const getInitialState = useCallback(() => {
    const page = pages.find((page) => `/${page.urlKey}` === `/${section}`);

    if (!page.hasOwnProperty('content') && !page.hasOwnProperty('headImage') && !loading) {
      getPageData(pathname);
    }
  }, [getPageData, pages, pathname, loading]);

  useEffect(() => {
    getInitialState();
    if (!isSSR()) {
      scrollTo({
        top:  0,
        left: 0,
      });
    }
  }, [section]);

  // page.meta уже содержит полностью резолвленный на бэке SeoMeta для того URL,
  // с которым страница была изначально загружена (PageModel::getAboutDoorsPageForSSR).
  // Но переключение вкладок ниже — чисто клиентская навигация без нового запроса
  // к серверу, а весь JSON вложенных вкладок (page.content) грузится один раз
  // вместе с родительской страницей — поэтому для АКТУАЛЬНОЙ вкладки meta
  // приходится пересчитывать здесь же, спускаясь по тому же дереву, что и
  // PageModel::getMetaFromJSON() на бэке (title/description/keywords/isIndexed).
  // canonical в JSON вкладок не хранится — та же политика self-canonical, что
  // и на бэке для вложенных вкладок (см. PageModel::getAboutDoorsPageForSSR).
  const _getMetaTags = useCallback((page, pathname) => {
    let data   = [],
        params = [];

    try {
      const {
              PAGE_TYPE,
              SECTIONS,
            } = JSON.parse(page.content);
      data    = PAGE_TYPE === staticPageConstants.PAGE_TYPE.STATIC_PAGE ? SECTIONS : SECTIONS[0];

      params = pathname.split('/')
        .filter((item) => item !== '')
        .slice(1);
    } catch (e) {
    }

    // canonical всегда self — meta.canonical с бэка относится к URL первой
    // загрузки страницы, а не к текущей (после клиентского переключения) вкладке
    const canonical = !isSSR() ? `${window.location.origin}${pathname}` : undefined;

    let { label } = page;
    // копия, а не ссылка — ниже meta.title и т.д. переопределяются точечно,
    // мутировать page.meta (общее состояние из pages[]) нельзя
    const meta = { ...page.meta };

    const getMeta = (param, content) => {
      let tabsWithRouter = content.find(section => section.COMPONENT === staticPageConstants.COMPONENTS.TABS_WITH_ROUTER.LABEL);

      if (tabsWithRouter) {
        let {
              DATA: {
                      CONTENT,
                      META = [],
                      URL,
                    },
            }         = tabsWithRouter;
        let index     = URL.findIndex(url => url === param);
        let nextIndex = params.findIndex(item => item === param) + 1;

        if (nextIndex < params.length) {
          return getMeta(params[nextIndex], CONTENT[index]);
        } else {
          if (index >= 0 && !!META[index]) {
            label            = META[index].LABEL || label;
            meta.title       = META[index].TITLE || meta.title;
            meta.description = META[index].DESCRIPTION || meta.description;
            meta.keywords    = META[index].KEYWORDS || meta.keywords;
            meta.isIndexed   = typeof META[index].IS_INDEXED === 'boolean' ? META[index].IS_INDEXED : meta.isIndexed;
          }

          return {
            label,
            meta: { ...meta, canonical },
          };
        }
      }
    };

    if (params.length > 0) {
      return getMeta(params[0], data) ?? { label, meta: { ...meta, canonical } };
    } else {
      return {
        label,
        meta: { ...meta, canonical },
      };
    }
  }, []);

  const changeTab = useCallback((tabsType, index) => {
    if (node.current) {
      if (tabsType === staticPageConstants.PAGE_TYPE.TABS_WITH_SCROLLING && tabs[index]) {
        const nav      = node.current.ownerDocument.getElementById('nav')
          .getBoundingClientRect();
        const header   = node.current.ownerDocument.getElementById('header')
          .getBoundingClientRect();
        const position = tabs[index].getBoundingClientRect();

        if (!isSSR()) {
          scrollTo({
            top:      position.top - nav.height - header.height,
            left:     0,
            behavior: 'smooth',
          });
        }
      }
    }
  }, [node.current, tabs]);

  const renderSections = useCallback(({
    PAGE_TYPE,
    SECTIONS,
  }) => {
    if (PAGE_TYPE === staticPageConstants.PAGE_TYPE.STATIC_PAGE) {
      return SECTIONS.map((section, index) => renderStaticSection(section, index));
    }

    return SECTIONS.map((tab, index) => {
      return ((
        <div
          ref={(node) => {
            if (!tabs.hasOwnProperty(index)) {
              tabs[inxed] = node;
            }
          }}
          key={index}
        >
          {tab.map((section, index) => renderStaticSection(section, index))}
        </div>
      ));
    });
  }, [node, renderStaticSection, tabs]);

  const renderPage = useCallback((page) => {
    const {
            content: Content,
          } = page;

    let content;
    try {
      content = JSON.parse(Content);
    } catch (e) {
    }

    if (!content) {
      return (
        <Notfound />
      );
    }

    return (
      <main className={page.template}>
        <article>
          {renderSections(content)}
        </article>
        <section className={'to-catalog'}>
          <div className={'container'}>
            <RenderButton
              type={'link'}
              uri={currentPage?.inCatalogLink || '/vhodnye-dveri'}
              label={currentPage?.inCatalogLabel || i18n.t('client_goToCatalog')}
              className={'fill'}
            />
            <ContactUs label={currentPage?.contactButtonName} />
          </div>
        </section>
        <ConsultationBlock maxWidth={'1212px'} />
      </main>
    );
  }, [currentPage, renderSections]);

  if (!page) {
    return (
      <Notfound />
    );
  }

  const {
          content: Content,
          headImage,
        }           = page;
  const headerProps = {
    transparent: false,
  };

  if (DV.isString(headImage)) {
    headerProps.transparent = true;
    headerProps.bg          = headImage;
  }

  try {
    const {
            PAGE_TYPE,
            PAGE_TABS,
          } = JSON.parse(Content);
    if (PAGE_TYPE !== staticPageConstants.PAGE_TYPE.STATIC_PAGE) {
      headerProps.doubleBtns = (
        <RenderHeaderTabs
          nav={PAGE_TABS.map(label => ({ label }))}
          onChange={(index) => changeTab(PAGE_TYPE, index)}
          active={0}
        />
      );
    }
  } catch (e) {
  }

  const {
          label,
          meta,
        } = _getMetaTags(page, pathname);

  return (
    <>
      <div ref={node} className={'page-content'}>
        <Meta {...meta} />
        {DV.isNotEmptyString(label) && (
          <PageHeader {...headerProps}>{label}</PageHeader>
        )}
        {page.hasOwnProperty('content') && page.hasOwnProperty('headImage') && (
          renderPage(page)
        )}
        {loading && (
          <Preloader />
        )}
      </div>
    </>
  );
};

export default AboutDoors;